import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Download, CheckCircle2, AlertCircle, Copy, History, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const LABELS = { properties: "Immobili", owners: "Proprietari", contacts: "Contatti/Investitori" };

export default function ImportsPage() {
  const [entity, setEntity] = useState("properties");
  const [preview, setPreview] = useState(null);
  const [skipDups, setSkipDups] = useState(true);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const fileRef = useRef();

  const loadHistory = async () => {
    const { data } = await api.get("/imports"); setHistory(data);
  };
  React.useEffect(() => { loadHistory(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("entity_type", entity);
    try {
      const { data } = await api.post("/imports/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`${data.valid_rows} righe valide, ${data.errors.length} errori, ${data.duplicates.length} duplicati`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { e.target.value = ""; }
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const rows = preview.preview;
      const { data } = await api.post("/imports/commit", { entity_type: preview.entity_type, rows, skip_duplicates: skipDups });
      toast.success(`${data.inserted} inseriti · ${data.skipped} saltati`);
      setPreview(null); loadHistory();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const rollback = async (id) => {
    if (!window.confirm("Annullare questa importazione? I record inseriti verranno rimossi.")) return;
    try {
      const { data } = await api.post(`/imports/${id}/rollback`, {});
      toast.success(`Rimossi ${data.deleted} record`); loadHistory();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dupLines = new Set((preview?.duplicates || []).map((d) => d.line));

  return (
    <div className="space-y-6" data-testid="imports-page">
      <header>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Importa dati</h1>
        <p className="text-muted-foreground text-sm mt-1">Carica immobili, proprietari e contatti da un CSV con anteprima, validazione, rilevamento duplicati e rollback.</p>
      </header>

      <Card className="border-border/60 p-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Tipo di dati</label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger className="mt-1 md:w-64" data-testid="entity-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="properties">Immobili</SelectItem>
                <SelectItem value="owners">Proprietari</SelectItem>
                <SelectItem value="contacts">Contatti / Investitori</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <a href={`${API}/imports/templates/${entity}`} className="inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="download-template">
            <Download className="w-4 h-4" /> Scarica template CSV
          </a>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="csv-file-input" />
          <Button onClick={() => fileRef.current?.click()} className="gap-2" data-testid="upload-csv">
            <Upload className="w-4 h-4" /> Carica CSV
          </Button>
        </div>
      </Card>

      {preview && (
        <Card className="border-border/60 p-6" data-testid="preview-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h3 className="font-heading text-lg font-medium">Anteprima {LABELS[preview.entity_type]}</h3>
              <div className="flex gap-3 mt-2 text-xs">
                <Badge className="bg-emerald-100 text-emerald-700 border-transparent">{preview.valid_rows} valide</Badge>
                <Badge className="bg-rose-100 text-rose-700 border-transparent">{preview.errors.length} errori</Badge>
                <Badge className="bg-amber-100 text-amber-700 border-transparent">{preview.duplicates.length} duplicati</Badge>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={skipDups} onChange={(e) => setSkipDups(e.target.checked)} data-testid="skip-dups" /> Salta duplicati</label>
              <Button variant="outline" onClick={() => setPreview(null)}>Annulla</Button>
              <Button onClick={commit} disabled={busy || preview.valid_rows === 0} data-testid="commit-import">{busy ? "Importazione…" : `Importa ${preview.valid_rows - dupLines.size} righe`}</Button>
            </div>
          </div>

          {preview.errors.length > 0 && (
            <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 dark:bg-rose-950/20 p-3">
              <div className="text-xs font-medium text-rose-700 mb-1 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Errori di validazione</div>
              <ul className="text-xs space-y-1 max-h-40 overflow-y-auto scrollbar-thin">
                {preview.errors.slice(0, 20).map((e, i) => <li key={i}><span className="tabular-nums text-muted-foreground">Riga {e.line}:</span> {e.errors.join(" · ")}</li>)}
              </ul>
            </div>
          )}

          <div className="max-h-[400px] overflow-auto scrollbar-thin border border-border/60 rounded-md">
            <Table>
              <TableHeader><TableRow><TableHead>Riga</TableHead>{(preview.columns.provided || []).slice(0, 6).map((c) => <TableHead key={c}>{c}</TableHead>)}<TableHead>Stato</TableHead></TableRow></TableHeader>
              <TableBody>
                {preview.preview.map((r) => {
                  const isDup = dupLines.has(r.line);
                  return (
                    <TableRow key={r.line} className={isDup ? "bg-amber-50/40 dark:bg-amber-950/20" : ""}>
                      <TableCell className="tabular-nums text-xs">{r.line}</TableCell>
                      {(preview.columns.provided || []).slice(0, 6).map((c) => <TableCell key={c} className="text-xs truncate max-w-[200px]">{String(r.data[c] ?? "")}</TableCell>)}
                      <TableCell>{isDup ? <Badge className="bg-amber-100 text-amber-700 border-transparent text-[10px]">Duplicato</Badge> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {history.length > 0 && (
        <Card className="border-border/60 p-6">
          <h3 className="font-heading text-lg font-medium mb-4 flex items-center gap-2"><History className="w-4 h-4 text-primary" /> Storico importazioni</h3>
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 p-3 border border-border/60 rounded-md text-sm" data-testid={`imp-${h.id}`}>
                <Badge variant="secondary" className="capitalize">{h.entity_type}</Badge>
                <div className="flex-1 min-w-0">
                  <div>{h.inserted} inseriti · {h.skipped} saltati {h.errors_count > 0 && <span className="text-rose-600">· {h.errors_count} errori</span>}</div>
                  <div className="text-xs text-muted-foreground">{h.user_name} · {new Date(h.created_at).toLocaleString("it-IT")}</div>
                </div>
                {h.rolled_back_at ? <Badge className="bg-slate-200 text-slate-600 border-transparent">Annullata</Badge> :
                  <Button variant="outline" size="sm" onClick={() => rollback(h.id)} className="gap-1" data-testid={`rollback-${h.id}`}><Undo2 className="w-3.5 h-3.5" /> Rollback</Button>
                }
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
